import React, {useRef} from 'react';
import {useDrag, useDrop} from 'react-dnd';
import StoryboardCellComponent from '@/views/storyboard/cell/StoryboardCellComponent';
import {Chart, ChartQuery, ChartSettings} from 'entities/chart';

interface DraggableStoryboardCellProps {
    id: number;
    index: number;
    chartQuery: ChartQuery;
    chart: ChartSettings;
    writeAccess: boolean;
    deleteChart: (index: number) => void;
    onNewChartSave: (chartData: Chart, toIndex?: number) => void;
    modifyChart: (chartData: Chart) => void;
    moveChart: (dragIndex: number, hoverIndex: number) => void;
    onChartMove: () => void;
    hasError?: boolean;
    isRearrangeMode: boolean;
}

interface DragItem {
    id: number;
    index: number;
    type: string;
}

const DraggableStoryboardCellComponent: React.FC<DraggableStoryboardCellProps> = (props) => {
    try {
        const {id, index, moveChart, onChartMove} = props;
        
        const ref = useRef<HTMLDivElement>(null!);
        
        const [{isDragging}, drag, dragPreview] = useDrag({
            type: 'chart',
            item: {type: 'chart', id, index},
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
        });

        const [, drop] = useDrop({
            accept: 'chart',
            hover: (draggedItem: DragItem) => {
                if (draggedItem.index !== index) {
                    moveChart(draggedItem.index, index);
                    draggedItem.index = index;
                }
            },
            drop: () => {
                onChartMove();
            },
        });

        drag(drop(ref));

        return (
            <StoryboardCellComponent
                {...props}
                isDragging={isDragging}
                innerRef={ref}
                connectDragPreview={dragPreview}
            />
        );
    } catch (error) {
        console.error('Error in DraggableStoryboardCellComponent:', error, props);
        // Fallback to non-draggable version if there's an error
        return (
            <StoryboardCellComponent
                {...props}
                isDragging={false}
                innerRef={useRef<HTMLDivElement>(null!)}
                hasError={true}
            />
        );
    }
};

export default DraggableStoryboardCellComponent;